import React, { useContext, useState, useEffect, useCallback, Fragment } from 'react';
import Layout from '../../components/layout/main';
import {
    Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
    Fade, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
    Table, TableBody, TableCell, TableHead, TableRow, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintIcon from '@mui/icons-material/Print';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/auth';
import { GlobalsContext } from '../../context/globals';
import { getData, postData } from '../../services/ajax';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { useSnackbar } from 'notistack';
import QrImage from '../../components/qr/QrImage';
import { parseCredencialQrUrl } from '../../utils/credencialUrl';

const ESTADO_CRED_COLOR = {
    ACTIVA: 'success',
    SUSPENDIDA: 'warning',
    REVOCADA: 'error',
};

function Page() {
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const globalsContext = useContext(GlobalsContext);
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const idPersonal = globalsContext.idEdit;

    const [tab, setTab] = useState(0);
    const [detalle, setDetalle] = useState(null);
    const [vinculaciones, setVinculaciones] = useState([]);
    const [credenciales, setCredenciales] = useState([]);
    const [areas, setAreas] = useState([]);
    const [funciones, setFunciones] = useState([]);

    // Form vinculacion
    const [vincArea, setVincArea] = useState('');
    const [vincTipo, setVincTipo] = useState('DESEMPENO');
    // Form funcion
    const [funcionSel, setFuncionSel] = useState('');

    // QR modal
    const [qrModal, setQrModal] = useState({ open: false, url: '', codigo: '', fecha_vencimiento: null });

    const cargarTodo = useCallback(async () => {
        if (!idPersonal) {
            navigate('/personal');
            return;
        }
        loadingModal.setOpen(true);
        try {
            const [d, v, c, ar, fn] = await Promise.all([
                getData(`personal/${idPersonal}`, usuario.token),
                getData(`personal/${idPersonal}/vinculaciones`, usuario.token),
                getData(`personal/${idPersonal}/credenciales`, usuario.token),
                getData('catalogos/areas', usuario.token),
                getData('catalogos/funciones', usuario.token),
            ]);
            if (d.error === '') setDetalle(d.data);
            if (v.error === '') setVinculaciones(Array.isArray(v.data) ? v.data : []);
            if (c.error === '') setCredenciales(Array.isArray(c.data) ? c.data : []);
            if (ar.error === '') setAreas(ar.data || []);
            if (fn.error === '') setFunciones(fn.data || []);
        } finally {
            loadingModal.setOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPersonal, usuario.token]);

    useEffect(() => { cargarTodo(); }, [cargarTodo]);

    const recargarVinc = useCallback(async () => {
        const v = await getData(`personal/${idPersonal}/vinculaciones`, usuario.token);
        if (v.error === '') setVinculaciones(Array.isArray(v.data) ? v.data : []);
        const d = await getData(`personal/${idPersonal}`, usuario.token);
        if (d.error === '') setDetalle(d.data);
    }, [idPersonal, usuario.token]);

    const recargarCred = useCallback(async () => {
        const c = await getData(`personal/${idPersonal}/credenciales`, usuario.token);
        if (c.error === '') setCredenciales(Array.isArray(c.data) ? c.data : []);
    }, [idPersonal, usuario.token]);

    const asignarVinculacion = async () => {
        if (!vincArea) { enqueueSnackbar('Seleccioná un área', { variant: 'warning' }); return; }
        loadingModal.setOpen(true);
        const resp = await postData(`personal/${idPersonal}/vinculaciones`, { id_area: Number(vincArea), tipo: vincTipo }, usuario.token);
        loadingModal.setOpen(false);
        if (resp.error === '') {
            enqueueSnackbar('Vinculación asignada', { variant: 'success' });
            setVincArea('');
            recargarVinc();
        } else {
            enqueueSnackbar(resp.error, { variant: 'error' });
        }
    };

    const asignarFuncion = async () => {
        if (!funcionSel) { enqueueSnackbar('Seleccioná una función', { variant: 'warning' }); return; }
        loadingModal.setOpen(true);
        const resp = await postData(`personal/${idPersonal}/funcion`, { id_funcion: Number(funcionSel) }, usuario.token);
        loadingModal.setOpen(false);
        if (resp.error === '') {
            enqueueSnackbar('Función asignada', { variant: 'success' });
            setFuncionSel('');
            cargarTodo();
        } else {
            enqueueSnackbar(resp.error, { variant: 'error' });
        }
    };

    const abrirModalQr = (data) => {
        setQrModal({
            open: true,
            url: data.url,
            codigo: data.codigo_publico,
            fecha_vencimiento: data.fecha_vencimiento || null,
        });
    };

    const emitirCredencial = async () => {
        loadingModal.setOpen(true);
        const resp = await postData(`personal/${idPersonal}/credenciales`, {}, usuario.token);
        loadingModal.setOpen(false);
        if (resp.error === '') {
            abrirModalQr(resp.data);
            recargarCred();
        } else {
            enqueueSnackbar(resp.error, { variant: 'error' });
        }
    };

    const verQr = async (cred) => {
        loadingModal.setOpen(true);
        const resp = await getData(`credenciales/${cred.id_credencial}/qr`, usuario.token);
        loadingModal.setOpen(false);
        if (resp.error === '') {
            abrirModalQr(resp.data);
        } else {
            enqueueSnackbar(resp.error, { variant: 'error' });
        }
    };

    const regenerarQr = (cred) => {
        alertModal.showModal(
            'Regenerar QR',
            'Esta acción generará un nuevo código QR y el anterior dejará de funcionar de inmediato. ' +
            'Si la credencial ya fue impresa en formato físico, deberá imprimirse nuevamente con el nuevo QR. ' +
            '¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const resp = await postData(`credenciales/${cred.id_credencial}/regenerar`, {}, usuario.token);
                loadingModal.setOpen(false);
                if (resp.error === '') {
                    abrirModalQr(resp.data);
                    recargarCred();
                    enqueueSnackbar('QR regenerado. El código anterior ya no es válido.', { variant: 'warning' });
                } else {
                    enqueueSnackbar(resp.error, { variant: 'error' });
                }
            });
    };

    const revocarCredencial = (cred) => {
        alertModal.showModal(
            'Revocar credencial',
            'La credencial dejará de ser válida de forma permanente. ¿Deseas continuar?',
            async function () {
                loadingModal.setOpen(true);
                const resp = await postData(`credenciales/${cred.id_credencial}/revocar`, { motivo: 'Revocada desde panel' }, usuario.token);
                loadingModal.setOpen(false);
                if (resp.error === '') {
                    enqueueSnackbar('Credencial revocada', { variant: 'success' });
                    recargarCred();
                } else {
                    enqueueSnackbar(resp.error, { variant: 'error' });
                }
            });
    };

    const abrirImpresionCredencial = (url) => {
        const parsed = parseCredencialQrUrl(url);
        if (!parsed) {
            enqueueSnackbar('URL de credencial inválida', { variant: 'error' });
            return;
        }
        window.open(`#/credencial/imprimir/${parsed.codigoPublico}/${parsed.token}?autoprint=1`, '_blank');
    };

    const imprimirCredencial = async (cred) => {
        loadingModal.setOpen(true);
        const resp = await getData(`credenciales/${cred.id_credencial}/qr`, usuario.token);
        loadingModal.setOpen(false);
        if (resp.error !== '') {
            enqueueSnackbar(resp.error, { variant: 'error' });
            return;
        }
        abrirImpresionCredencial(resp.data.url);
    };

    const copiarUrl = async () => {
        try {
            await navigator.clipboard.writeText(qrModal.url);
            enqueueSnackbar('URL copiada', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('No se pudo copiar', { variant: 'error' });
        }
    };

    const imprimirQr = () => {
        abrirImpresionCredencial(qrModal.url);
    };

    const persona = detalle?.persona;

    return (
        <Fragment>
            <Layout nombrePagina="Detalle de Personal">
                <Fade in={true} timeout={900}>
                    <Grid size={12}>
                        <Paper sx={{ p: 3, mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                    <Typography variant="h6">{persona ? `${persona.apellido}, ${persona.nombre}` : 'Empleado'}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Legajo: {detalle?.legajo || '-'} · Estado: {detalle?.estado?.nombre || '-'}
                                    </Typography>
                                </Box>
                                <Button onClick={() => navigate('/personal')}>Volver al listado</Button>
                            </Box>

                            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mt: 2 }}>
                                <Tab label="Datos" />
                                <Tab label="Vinculaciones" />
                                <Tab label="Credenciales QR" />
                            </Tabs>
                            <Divider />

                            {/* TAB DATOS */}
                            {tab === 0 && (
                                <Box sx={{ pt: 2 }}>
                                    <Grid container spacing={2}>
                                        <Dato label="Apellido y nombre" value={persona ? `${persona.apellido}, ${persona.nombre}` : '-'} />
                                        <Dato label="Documento" value={persona ? `${persona.tipo_documento || ''} ${persona.dni || ''}` : '-'} />
                                        <Dato label="CUIL" value={persona?.cuil || '-'} />
                                        <Dato label="Legajo" value={detalle?.legajo || '-'} />
                                        <Dato label="Estado" value={detalle?.estado?.nombre || '-'} />
                                        <Dato label="Pertenencia actual" value={detalle?.pertenencia_actual ? `${detalle.pertenencia_actual.area} (CUOF ${detalle.pertenencia_actual.cuof || '-'})` : '-'} />
                                        <Dato label="Desempeño actual" value={detalle?.desempeno_actual ? `${detalle.desempeno_actual.area} (CUOF ${detalle.desempeno_actual.cuof || '-'})` : '-'} />
                                        <Dato label="Función actual" value={detalle?.funcion_actual?.nombre || '-'} />
                                    </Grid>
                                </Box>
                            )}

                            {/* TAB VINCULACIONES */}
                            {tab === 1 && (
                                <Box sx={{ pt: 2 }}>
                                    <Typography variant="subtitle1" gutterBottom>Asignar vinculación de área</Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid size={{ xs: 12, sm: 5 }}>
                                            <TextField select fullWidth variant="standard" label="Área" value={vincArea} onChange={(e) => setVincArea(e.target.value)}>
                                                {areas.map((a) => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 4 }}>
                                            <TextField select fullWidth variant="standard" label="Tipo" value={vincTipo} onChange={(e) => setVincTipo(e.target.value)}>
                                                <MenuItem value="PERTENENCIA">Pertenencia</MenuItem>
                                                <MenuItem value="DESEMPENO">Desempeño</MenuItem>
                                                <MenuItem value="ADSCRIPCION">Adscripción</MenuItem>
                                                <MenuItem value="COMISION_SERVICIO">Comisión de servicio</MenuItem>
                                                <MenuItem value="TRASLADO_TEMPORAL">Traslado temporal</MenuItem>
                                                <MenuItem value="AFECTACION">Afectación</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Button variant="contained" color="secondary" onClick={asignarVinculacion} fullWidth>Asignar</Button>
                                        </Grid>
                                    </Grid>

                                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>Asignar función actual</Typography>
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            <TextField select fullWidth variant="standard" label="Función" value={funcionSel} onChange={(e) => setFuncionSel(e.target.value)}>
                                                {funciones.map((f) => <MenuItem key={f.id} value={f.id}>{f.nombre}</MenuItem>)}
                                            </TextField>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                            <Button variant="contained" color="secondary" onClick={asignarFuncion} fullWidth>Asignar</Button>
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="subtitle1" gutterBottom>Historial de vinculaciones</Typography>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Tipo</TableCell>
                                                <TableCell>Área</TableCell>
                                                <TableCell>CUOF</TableCell>
                                                <TableCell>Desde</TableCell>
                                                <TableCell>Hasta</TableCell>
                                                <TableCell>Actual</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {vinculaciones.map((v) => (
                                                <TableRow key={v.id_vinculacion}>
                                                    <TableCell>{v.tipo_nombre}</TableCell>
                                                    <TableCell>{v.area}</TableCell>
                                                    <TableCell>{v.cuof || '-'}</TableCell>
                                                    <TableCell>{v.fecha_desde || '-'}</TableCell>
                                                    <TableCell>{v.fecha_hasta || '-'}</TableCell>
                                                    <TableCell>{v.es_actual ? <Chip size="small" color="success" label="Sí" /> : 'No'}</TableCell>
                                                </TableRow>
                                            ))}
                                            {vinculaciones.length === 0 && (
                                                <TableRow><TableCell colSpan={6}>Sin vinculaciones</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}

                            {/* TAB CREDENCIALES */}
                            {tab === 2 && (
                                <Box sx={{ pt: 2 }}>
                                    <Box sx={{ mb: 2 }}>
                                        <Button variant="contained" color="secondary" startIcon={<QrCode2Icon />} onClick={emitirCredencial}>
                                            Emitir nueva credencial
                                        </Button>
                                        {detalle && !detalle.estado?.permite_credencial && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                                                El estado actual del personal no permite emitir credencial.
                                            </Typography>
                                        )}
                                    </Box>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código público</TableCell>
                                                <TableCell>Estado</TableCell>
                                                <TableCell>Emisión</TableCell>
                                                <TableCell>Vencimiento</TableCell>
                                                <TableCell align="right">Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {credenciales.map((c) => (
                                                <TableRow key={c.id_credencial}>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{c.codigo_publico}</TableCell>
                                                    <TableCell><Chip size="small" color={ESTADO_CRED_COLOR[c.estado] || 'default'} label={c.estado} /></TableCell>
                                                    <TableCell>{c.fecha_emision ? new Date(c.fecha_emision).toLocaleString('es-AR') : '-'}</TableCell>
                                                    <TableCell>{c.fecha_vencimiento ? new Date(c.fecha_vencimiento).toLocaleDateString('es-AR') : 'Sin vencimiento'}</TableCell>
                                                    <TableCell align="right">
                                                        {c.estado === 'ACTIVA' && (
                                                            <CredencialAccionesMenu
                                                                onVerQr={() => verQr(c)}
                                                                onImprimir={() => imprimirCredencial(c)}
                                                                onRegenerar={() => regenerarQr(c)}
                                                                onRevocar={() => revocarCredencial(c)}
                                                            />
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {credenciales.length === 0 && (
                                                <TableRow><TableCell colSpan={5}>Sin credenciales emitidas</TableCell></TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                </Fade>
            </Layout>

            {/* MODAL QR */}
            <Dialog open={qrModal.open} onClose={() => setQrModal({ open: false, url: '', codigo: '', fecha_vencimiento: null })} maxWidth="xs" fullWidth>
                <DialogTitle>Credencial QR</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Este QR es permanente mientras la credencial esté activa. Podés volver a verlo o imprimirlo con "Ver QR" sin invalidar el código anterior.
                        </Typography>
                        {qrModal.fecha_vencimiento && (
                            <Typography variant="caption" color="text.secondary">
                                Vencimiento de la credencial: {new Date(qrModal.fecha_vencimiento).toLocaleDateString('es-AR')}
                            </Typography>
                        )}
                        {!qrModal.fecha_vencimiento && (
                            <Typography variant="caption" color="text.secondary">
                                Credencial sin fecha de vencimiento.
                            </Typography>
                        )}
                        <QrImage value={qrModal.url} size={240} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<ContentCopyIcon />} onClick={copiarUrl}>Copiar URL</Button>
                    <Button startIcon={<PrintIcon />} onClick={imprimirQr}>Imprimir</Button>
                    <Button onClick={() => setQrModal({ open: false, url: '', codigo: '', fecha_vencimiento: null })}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}

function CredencialAccionesMenu({ onVerQr, onImprimir, onRegenerar, onRevocar }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const abierto = Boolean(anchorEl);

    const cerrar = () => setAnchorEl(null);

    const ejecutar = (accion) => {
        cerrar();
        accion();
    };

    return (
        <>
            <IconButton
                size="small"
                aria-label="Acciones de credencial"
                aria-controls={abierto ? 'credencial-acciones-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={abierto ? 'true' : undefined}
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                <MoreVertRoundedIcon />
            </IconButton>
            <Menu
                id="credencial-acciones-menu"
                anchorEl={anchorEl}
                open={abierto}
                onClose={cerrar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={() => ejecutar(onVerQr)}>
                    <ListItemIcon><QrCode2Icon fontSize="small" /></ListItemIcon>
                    <ListItemText>Ver QR</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => ejecutar(onImprimir)}>
                    <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Imprimir credencial</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => ejecutar(onRegenerar)}>
                    <ListItemIcon><RefreshIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Regenerar QR</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => ejecutar(onRevocar)} sx={{ color: 'error.main' }}>
                    <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText>Revocar</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}

function Dato({ label, value }) {
    return (
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="body1">{value}</Typography>
        </Grid>
    );
}

export default Page;
